import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import { AlbumPartition } from '../../../../models/SpaceStore';
import { WebBoosterType } from '../../../../models/UserStore';
import {
  AppBar,
  FolderBreadcrumbRow,
  FolderBreadcrumbVariant,
  LightSwipe,
  StationCell,
  StationCellProps,
  SvgIcon,
} from '../../../Molecules';

import styles from './AIMemoryBoostVer2Template.module.css';

type AIMemoryBoostVer2TemplateProps = {
  selectedPeriod: string;
  webBoosterType: WebBoosterType | undefined;
  breadcrumbs: AlbumPartition[];
  completedGameCount: number;
  avgScore: number;
  needToReviseAgainItems?: AIMemoryBoostVer2CellProps[];
  needToReviseNowItems?: AIMemoryBoostVer2CellProps[];
  toBeRevisedLaterItems?: AIMemoryBoostVer2CellProps[];
  recentlyRevisedItems?: AIMemoryBoostVer2CellProps[];
  onClickPeriod: () => void;
  onClickBreadcrumb: () => void;
  onClickStartAIRevision: () => void;
  onClickPracticeDetail: () => void;
  onClickPerformanceReport: () => void;
};

export const AIMemoryBoostVer2Template = (
  props: AIMemoryBoostVer2TemplateProps,
) => {
  const {
    selectedPeriod,
    webBoosterType,
    breadcrumbs,
    needToReviseAgainItems,
    needToReviseNowItems,
    toBeRevisedLaterItems,
    recentlyRevisedItems,
    avgScore,
    completedGameCount,
    onClickPeriod,
    onClickBreadcrumb,
    onClickStartAIRevision,
    onClickPracticeDetail,
    onClickPerformanceReport,
  } = props;
  const { t } = useTranslation();
  const [needToReviseAgainCellNum, setNeedToReviseAgainCellNum] = useState(1);
  const [needToReviseNowCellNum, setNeedToReviseNowCellNum] = useState(5);
  const [toBeRevisedCellNum, setToBeRevisedCellNum] = useState(1);
  const [recentlyRevisedCellNum, setRecentlyRevisedCellNum] = useState(1);

  return (
    <div className={styles.pageContainer}>
      <AppBar
        title={t('Auto-Scheduled Daily Practice')}
        trailing={
          <div
            className={`${styles.hoverStyle}`}
            onClick={onClickPracticeDetail}
          >
            <SvgIcon
              name={SVGIconType.GameInstruction}
              width={24}
              height={24}
              color={'#808080'}
            />
          </div>
        }
      />
      {/* <div className={styles.titleContainer}>
        <div className={styles.titleText}>{t('Details')}</div>
        <div className={`${styles.hoverStyle}`} onClick={onClickPracticeDetail}>
          <SvgIcon
            name={SVGIconType.GameInstruction}
            width={24}
            height={24}
            color={'#333'}
          />
        </div>
      </div> */}

      <div className={styles.folderBreadcrumbRow}>
        <div>{t('Path: ')}</div>
        <FolderBreadcrumbRow
          webBoosterType={webBoosterType}
          breadcrumbs={breadcrumbs}
          variant={FolderBreadcrumbVariant.Black}
          onClickBreadcrumb={onClickBreadcrumb}
        />
      </div>

      <div className={`${styles.hContainer} ${styles.whiteRoundContainer}`}>
        <div
          className={`${styles.vContainer} ${styles.hoverStyle}`}
          onClick={onClickPerformanceReport}
        >
          <div className={styles.blackTitle}>{t('Practice Completed')}</div>
          <div className={styles.hContainer}>
            <SvgIcon
              name={SVGIconType.PracticeTick}
              width={26}
              height={26}
              color={'#333'}
            />
            <div className={styles.reportNumber}>{`${completedGameCount}`}</div>
            <SvgIcon
              name={SVGIconType.Arrow}
              width={16}
              height={16}
              color={'#808080'}
            />
          </div>
        </div>
        <div
          className={`${styles.vContainer} ${styles.hoverStyle}`}
          onClick={onClickPerformanceReport}
        >
          <div className={styles.blackTitle}>{t('Average Score')}</div>
          <div className={styles.hContainer}>
            <SvgIcon
              name={SVGIconType.DoubleScore}
              width={26}
              height={26}
              color={'#333'}
            />
            <div className={styles.reportNumber}>{avgScore}</div>
            <SvgIcon
              name={SVGIconType.DirectArrow}
              width={24}
              height={24}
              color={'#00a698'}
              className={styles.directArrowRotated}
            />
            <SvgIcon
              name={SVGIconType.Arrow}
              width={16}
              height={16}
              color={'#808080'}
            />
          </div>
        </div>
      </div>

      <div className={styles.whiteBannerContainer}>
        <div className={styles.greenCardHeader}>{t('Long-Term Memory')}</div>
        <div className={styles.hContainer}>
          <SvgIcon
            name={SVGIconType.Brain}
            width={24}
            height={24}
            color={'#ff7300'}
          />
          <div className={styles.orangeText}>{`${10}%`}</div>
          <SvgIcon
            name={SVGIconType.DirectArrow}
            width={24}
            height={24}
            color={'#808080'}
          />
          <SvgIcon
            name={SVGIconType.Brain}
            width={24}
            height={24}
            color={'#00a698'}
          />
          <div className={styles.cyanText}>{`${90}%+`}</div>
        </div>
        <div className={styles.periodSection} onClick={onClickPeriod}>
          <div className={styles.periodText}>
            <span className={styles.periodTitle}>
              {t('Newly Added Practice: ')}
            </span>
            <div className={styles.practicePeriodRowText}>
              <SvgIcon
                name={SVGIconType.Period}
                className={styles.periodIcon}
              />
              <span className={styles.selectedPeriod}>{t(selectedPeriod)}</span>
            </div>
          </div>
          <SvgIcon
            name={SVGIconType.Triangle}
            className={styles.triangleIcon}
          />
        </div>

        <div
          className={styles.buttonStartNowContainer}
          onClick={onClickStartAIRevision}
        >
          <SvgIcon
            name={SVGIconType.MemoryWithAI}
            className={styles.buttonAiIcon}
          />
          <div className={styles.buttonWhiteText}>{t('Start!')}</div>
          <SvgIcon name={SVGIconType.Play} className={styles.buttonPlayIcon} />
          <LightSwipe height={38} />
        </div>

        <div className={styles.textMinor}>
          {t(
            'Auto-Scheduled Daily Practice prioritizes your practice according to your previous score and completion time.',
          )}
        </div>
      </div>

      <div className={styles.topSection}>
        <div className={styles.startNowContainer}>
          <SvgIcon name={SVGIconType.MemoryWithAI} className={styles.aiIcon} />
          <div className={styles.greyText}>{t('Start!')}</div>
        </div>

        <div className={styles.subTitle}>{t('Priority of All Practice')}</div>
      </div>

      {needToReviseAgainItems && (
        <div className={`${styles.revisionContainer} ${styles.noMarginTop}`}>
          <div className={`${styles.revisionBanner} ${styles.purple}`}>
            {t('Top Priority')}
            <div className={styles.bannerBottom}>
              {t('Score < 90% · Practice Again')}
            </div>
          </div>
          <div className={styles.cellArea}>
            {needToReviseAgainItems
              .slice(0, needToReviseAgainCellNum)
              .map((item, index) => (
                <AIMemoryBoostVer2Cell key={index} {...item} />
              ))}
          </div>
          {needToReviseAgainCellNum <
            Math.min(needToReviseAgainItems.length, 30) && (
            <div className={styles.showMoreSpaceArea}>
              <div
                className={styles.showMoreContainer}
                onClick={() => {
                  setNeedToReviseAgainCellNum(needToReviseAgainCellNum + 5);
                }}
              >
                <SvgIcon
                  name={SVGIconType.Arrow}
                  className={styles.arrowIcon}
                />
                <div className={styles.showMoreText}>{t('Show More')}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className={`${styles.revisionContainer} ${
          !needToReviseAgainItems && styles.noMarginTop
        }`}
      >
        <div className={`${styles.revisionBanner} ${styles.strongRed}`}>
          {t('High Priority')}
          <div className={styles.bannerBottom}>{t('Revise Now')}</div>
        </div>

        {needToReviseNowItems ? (
          <>
            <div className={styles.cellArea}>
              {needToReviseNowItems
                .slice(0, needToReviseNowCellNum)
                .map((item, index) => (
                  <AIMemoryBoostVer2Cell key={index} {...item} />
                ))}
            </div>
            {needToReviseNowCellNum < needToReviseNowItems.length && (
              <div className={styles.showMoreSpaceArea}>
                <div
                  className={styles.showMoreContainer}
                  onClick={() => {
                    setNeedToReviseNowCellNum(needToReviseNowCellNum + 5);
                  }}
                >
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    className={styles.arrowIcon}
                  />
                  <div className={styles.showMoreText}>{t('Show More')}</div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyContainer}>
            <SvgIcon
              name={SVGIconType.MemoryWithAI}
              color={'#333333'}
              className={styles.emptyIcon}
            />
            <p className={styles.emptyText}>{t('No Scheduled Practice')}</p>
          </div>
        )}
      </div>

      <div className={styles.revisionContainer}>
        <div className={`${styles.revisionBanner} ${styles.strongBlue}`}>
          {t('Waiting List')}
          <div className={styles.bannerBottom}>{t('Revised Later')}</div>
        </div>

        {toBeRevisedLaterItems ? (
          <>
            <div className={styles.cellArea}>
              {toBeRevisedLaterItems
                .slice(0, toBeRevisedCellNum)
                .map((item, index) => (
                  <AIMemoryBoostVer2Cell key={index} {...item} />
                ))}
            </div>
            {toBeRevisedCellNum < toBeRevisedLaterItems.length && (
              <div className={styles.showMoreSpaceArea}>
                <div
                  className={styles.showMoreContainer}
                  onClick={() => {
                    setToBeRevisedCellNum(toBeRevisedCellNum + 5);
                  }}
                >
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    className={styles.arrowIcon}
                  />
                  <div className={styles.showMoreText}>{t('Show More')}</div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyContainer}>
            <SvgIcon
              name={SVGIconType.MemoryWithAI}
              color={'#333333'}
              className={styles.emptyIcon}
            />
            <p className={styles.emptyText}>{t('No Scheduled Practice')}</p>
          </div>
        )}
      </div>

      {recentlyRevisedItems && (
        <div className={styles.revisionContainer}>
          <div className={`${styles.revisionBanner} ${styles.strongGreen}`}>
            {t('Recently Revised')}
          </div>
          <div className={styles.cellArea}>
            {recentlyRevisedItems
              .slice(0, recentlyRevisedCellNum)
              .map((item, index) => (
                <AIMemoryBoostVer2Cell key={index} {...item} />
              ))}
          </div>
          {recentlyRevisedCellNum < recentlyRevisedItems.length && (
            <div className={styles.showMoreSpaceArea}>
              <div
                className={styles.showMoreContainer}
                onClick={() => {
                  setRecentlyRevisedCellNum(recentlyRevisedCellNum + 5);
                }}
              >
                <SvgIcon
                  name={SVGIconType.Arrow}
                  className={styles.arrowIcon}
                />
                <div className={styles.showMoreText}>{t('Show More')}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export type AIMemoryBoostVer2CellProps = {
  stationCell: StationCellProps;
  priority?: number;
  recentPlayedDate: string;
  daysFromPlaying: string;
  recent3GamesAverage: string;
  backgroundColour: AIMemoryBoostVer2CellColour;
  isReviseLater?: boolean;
};

export enum AIMemoryBoostVer2CellColour {
  red = 'red',
  orange = 'orange',
  yellow = 'yellow',
  green = 'green',
  blue = 'blue',
  navyBlue = 'navyBlue',
  purple = 'purple',
  turquoise = 'turquoise',
  white = 'white',
}

export const AIMemoryBoostVer2Cell = (props: AIMemoryBoostVer2CellProps) => {
  const {
    stationCell,
    priority,
    recentPlayedDate,
    daysFromPlaying,
    recent3GamesAverage,
    backgroundColour,
    isReviseLater,
  } = props;
  const { t } = useTranslation();

  let bgColourStyling;
  switch (backgroundColour) {
    case AIMemoryBoostVer2CellColour.red:
      bgColourStyling = styles.red;
      break;
    case AIMemoryBoostVer2CellColour.orange:
      bgColourStyling = styles.orange;
      break;
    case AIMemoryBoostVer2CellColour.yellow:
      bgColourStyling = styles.yellow;
      break;
    case AIMemoryBoostVer2CellColour.green:
      bgColourStyling = styles.green;
      break;
    case AIMemoryBoostVer2CellColour.blue:
      bgColourStyling = styles.blue;
      break;
    case AIMemoryBoostVer2CellColour.navyBlue:
      bgColourStyling = styles.navyBlue;
      break;
    case AIMemoryBoostVer2CellColour.purple:
      bgColourStyling = styles.purpleText;
      break;
    case AIMemoryBoostVer2CellColour.turquoise:
      bgColourStyling = styles.turquoise;
      break;
    default:
      bgColourStyling = styles.white;
      break;
  }

  return (
    <div className={`${styles.cellContainer} ${bgColourStyling}`}>
      <div className={styles.priorityTopContainer}>
        <div className={styles.priorityContainer}>
          <div className={styles.priorityText}>{t('Priority: ')}</div>
          <div
            className={`${styles.priorityBubble} ${
              priority ? styles.priorityRed : styles.priorityGrey
            }`}
          >
            {priority ? priority : '-'}
          </div>
        </div>

        <div className={styles.rightContainer}>
          <div className={styles.averageText}>{t('Average: ')}</div>
          <SvgIcon
            name={SVGIconType.Score}
            width={24}
            height={24}
            color={'#333'}
          />
          <div className={styles.averageTextNum}>{recent3GamesAverage}</div>
        </div>
      </div>

      <div className={`${styles.revisionTimeContainer}`}>
        <SvgIcon
          name={SVGIconType.Clock}
          width={18}
          height={18}
          color={isReviseLater ? '#808080' : '#05656b'}
        />
        <div className={`${styles.dateTextArea}`}>
          <div
            className={`${
              isReviseLater ? styles.darkGreenTextGreyVer : styles.darkGreenText
            }`}
          >
            {t('Practice: ')}
            {t(recentPlayedDate)}
          </div>
          <div
            className={`${
              isReviseLater
                ? styles.darkGreenTextGreyVerMinor
                : styles.darkGreenTextMinor
            }`}
          >
            {` (${daysFromPlaying})`}
          </div>
        </div>
      </div>
      <StationCell {...stationCell} />
    </div>
  );
};
